import React, {useContext, useEffect, useState} from 'react';
import itemTypeToEmoji from 'util/itemTypeToEmoji';
import {MarketContext} from 'util/market/MarketContext';
import 'styles/lootbox/RarityProgress.css';
import itemRarityColors from 'util/itemRarityColors';
import {useSessionConfiguration} from 'util/SessionConfigurationContext';

const RarityProgressBar = ({rarity, ownedCount, totalCount, disableAnimations}) => {
    const [isHovered, setIsHovered] = useState(false);
    const {isTouch} = useSessionConfiguration();

    const rarityColor = itemRarityColors(null, rarity);
    const insideBackgroundColor = rarity !== 1 ? rarityColor.textColor : rarityColor.actionButtonColor;

    const onClick = () => {
        if (isTouch) {
            setIsHovered(!isHovered);
        }
    };

    return (
        <div
            className="rarity-progress-outside"
            style={{backgroundColor: rarityColor.backgroundColor}}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => onClick()}
        >
            <div
                className="rarity-progress-inside"
                style={{
                    transform: `translate(${(ownedCount / totalCount) * 100}%, 0)`,
                    backgroundColor: insideBackgroundColor,
                    transition: disableAnimations ? 'none' : 'transform 1s ease',
                }}
            />
            {isHovered ? (
                <>
                    <div className="rarity-progress-middle">
                        {itemTypeToEmoji.getRarityFromNumber(rarity)}
                    </div>
                    <div className="rarity-progress-right">
                        {ownedCount} / {totalCount}
                    </div>
                </>
            ) : (
                <div className="rarity-progress-middle">
                    {ownedCount} / {totalCount}
                </div>
            )}
        </div>
    );
};

const RarityProgress = () => {
    const {allItems} = useContext(MarketContext);
    const [myItems, setMyItems] = useState([]);
    const [rarityDistribution, setRarityDistribution] = useState({});
    const [globalRarityDistribution, setGlobalRarityDistribution] = useState({});
    const [showIndex, setShowIndex] = useState(-1);
    const [disableAnimations, setDisableAnimations] = useState(true);

    useEffect(() => {
        // Disable animations for 2 seconds
        const timeout = setTimeout(() => {
            setDisableAnimations(false);
        }, 200);
        return () => clearTimeout(timeout); // Clean up timeout on unmount
    }, []);

    useEffect(() => {
        const rarityCounts = {};
        for (let i = 1; i <= 5; i++) {
            rarityCounts[i] = itemTypeToEmoji.getCountOfRarityLevel(i);
        }
        setGlobalRarityDistribution(rarityCounts);
    }, []);

    useEffect(() => {
        if (allItems.length > 0) {
            const items = allItems.filter(i => i.hasUserItem()).map(i => i.itemType);
            setMyItems(items);
        }
    }, [allItems]);

    useEffect(() => {
        const distribution = myItems.reduce((acc, itemType) => {
            const rarityLevel = itemTypeToEmoji.getRarityLevel(itemType);
            acc[rarityLevel] = (acc[rarityLevel] || 0) + 1;
            return acc;
        }, {});

        setRarityDistribution(distribution);
    }, [myItems]);

    useEffect(() => {
        const highestKey = Object.keys(rarityDistribution).length
            ? Math.max(...Object.keys(rarityDistribution).map(Number))
            : 0;

        setShowIndex(highestKey);
    }, [rarityDistribution]);

    return (
        <div style={{paddingTop: '12px', width: '100%'}}>
            {[1, 2, 3, 4, 5]
                .filter(rarity => rarity <= showIndex + 1) // Filter out lower progress bars
                .map(rarity => (
                    <RarityProgressBar
                        key={rarity}
                        rarity={rarity}
                        ownedCount={rarityDistribution[rarity] || 0}
                        totalCount={globalRarityDistribution[rarity]}
                        disableAnimations={disableAnimations}
                    />
                ))}
        </div>
    );
};

export default RarityProgress;
